/**
 * The component library.
 *
 * Deliberately small and plain. Every screen is built from these, so a change to
 * spacing or focus behaviour happens once. Nothing here reaches for a raw colour —
 * only the semantic tokens in index.css — which is what makes dark mode a single
 * definition rather than a per-component afterthought.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import type { StatusTone } from '@asp/shared';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'approve' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
  full?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-navy-900 text-white hover:bg-navy-800 active:bg-navy-950 disabled:bg-navy-900/40 shadow-panel',
  secondary:
    'border border-[var(--border-default)] bg-[var(--surface-1)] text-[var(--ink-primary)] ' +
    'hover:bg-[var(--surface-3)] active:bg-[var(--surface-3)]',
  ghost: 'text-[var(--ink-secondary)] hover:bg-[var(--surface-3)] hover:text-[var(--ink-primary)]',
  // Emerald is reserved for approval. If it were the default primary, approving
  // would look like every other button on the page.
  approve:
    'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 ' +
    'disabled:bg-emerald-600/40 shadow-panel',
  danger:
    'border border-[var(--color-critical)] text-[var(--color-critical)] bg-transparent ' +
    'hover:bg-[var(--color-critical)] hover:text-white',
};

const BUTTON_SIZES = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  // 40px on md and 44px on lg: lg is used for the approval actions, which are
  // tapped on a phone (docs/07).
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, full, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className ?? 'size-5')} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  children,
  className,
  title,
  description,
  actions,
  padded = true,
}: {
  children?: ReactNode;
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  padded?: boolean;
}) {
  return (
    <section
      className={cx(
        'rounded-card border border-[var(--border-subtle)] bg-[var(--surface-1)] shadow-panel',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="truncate text-sm font-semibold text-[var(--ink-primary)]">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-[var(--ink-muted)]">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'p-4' : undefined}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

const TONE_STYLES: Record<StatusTone, string> = {
  neutral: 'bg-[var(--surface-3)] text-[var(--ink-secondary)] border-[var(--border-default)]',
  info: 'bg-navy-50 text-navy-700 border-navy-200 dark:bg-navy-900/40 dark:text-navy-200 dark:border-navy-700',
  pending: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700',
  warning: 'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-800',
  danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800',
};

/**
 * Status is never colour alone: the badge always carries its label, and a dot
 * gives a second, non-colour cue.
 */
export function Badge({
  tone = 'neutral',
  children,
  dot = true,
}: {
  tone?: StatusTone;
  children: ReactNode;
  dot?: boolean;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[12px] font-medium whitespace-nowrap',
        TONE_STYLES[tone],
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx('block', className)}>
      <span className="mb-1 flex items-baseline gap-1.5 text-[13px] font-medium text-[var(--ink-primary)]">
        {label}
        {required && (
          <span className="text-[var(--color-critical)]" aria-label="required">
            *
          </span>
        )}
      </span>
      {children}
      {/* Error replaces the hint rather than stacking, so the field never grows. */}
      {error ? (
        <span className="mt-1 block text-[12px] font-medium text-[var(--color-critical)]">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-[12px] text-[var(--ink-muted)]">{hint}</span>
      ) : null}
    </label>
  );
}

const CONTROL_CLASS =
  'w-full rounded-md border border-[var(--border-default)] bg-[var(--surface-1)] px-3 ' +
  'text-sm text-[var(--ink-primary)] placeholder:text-[var(--ink-muted)] ' +
  'focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/20 ' +
  'disabled:bg-[var(--surface-3)] disabled:text-[var(--ink-muted)]';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cx(CONTROL_CLASS, 'h-10', invalid && 'border-[var(--color-critical)]', className)}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(CONTROL_CLASS, 'h-10 pr-8', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Textarea({
  className,
  rows = 3,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={rows} className={cx(CONTROL_CLASS, 'py-2', className)} {...rest} />;
}

// ---------------------------------------------------------------------------
// Table
//
// Wrapped in its own horizontal scroll container: a wide table must scroll
// itself, never make the page scroll sideways.
// ---------------------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={cx('w-full min-w-[36rem] border-collapse text-sm', className)}>{children}</table>
    </div>
  );
}

export function Th({
  children,
  numeric,
  className,
}: {
  children?: ReactNode;
  numeric?: boolean;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cx(
        'border-b border-[var(--border-default)] px-3 py-2 text-[12px] font-semibold uppercase tracking-wide',
        'text-[var(--ink-muted)]',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  numeric,
  className,
}: {
  children?: ReactNode;
  numeric?: boolean;
  className?: string;
}) {
  return (
    <td
      className={cx(
        'border-b border-[var(--border-subtle)] px-3 py-2.5 align-middle',
        numeric && 'text-right tabular',
        className,
      )}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cx(onClick && 'cursor-pointer hover:bg-[var(--surface-2)]', className)}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Empty, loading and error states
//
// Every list has all three. An empty table with no explanation is the most common
// way a working system looks broken.
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-[var(--ink-muted)]">{icon}</div>}
      <p className="text-sm font-semibold text-[var(--ink-primary)]">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-[13px] text-[var(--ink-muted)]">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-[var(--ink-muted)]">
      <Spinner className="size-4" />
      <span className="text-[13px]">{label}…</span>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  const hint = (error as { hint?: string } | null)?.hint;

  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <p className="text-sm font-semibold text-[var(--color-critical)]">{message}</p>
      {hint && <p className="mt-1 max-w-md text-[13px] text-[var(--ink-muted)]">{hint}</p>}
      {onRetry && (
        <Button size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline banner
// ---------------------------------------------------------------------------

export function Banner({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  title?: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const styles = {
    info: 'border-navy-200 bg-navy-50 text-navy-900 dark:border-navy-700 dark:bg-navy-900/40 dark:text-navy-100',
    warning:
      'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100',
    danger: 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100',
    success:
      'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100',
  }[tone];

  return (
    <div className={cx('rounded-md border px-3 py-2.5 text-[13px]', styles)} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title && <p className="font-semibold">{title}</p>}
          {children && <div className={title ? 'mt-0.5' : undefined}>{children}</div>}
        </div>
        {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page scaffolding
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-5">
      {breadcrumb && <div className="mb-1.5 text-[13px] text-[var(--ink-muted)]">{breadcrumb}</div>}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-[var(--ink-primary)]">{title}</h1>
          {description && <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** Label/value pair, used all over the request and order detail screens. */
export function DetailItem({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-[12px] font-medium uppercase tracking-wide text-[var(--ink-muted)]">{label}</dt>
      <dd className="mt-0.5 text-sm text-[var(--ink-primary)]">{children}</dd>
    </div>
  );
}
